import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TransactionForm from './TransactionForm';
import { Customer, CreateTransaction, Transaction } from '../types';
import { customersAPI, transactionsAPI } from '../services/api';
import { useTransactionDirections } from '../hooks/useTransactionDirections';
import { useTransactionTypes } from '../hooks/useTransactionTypes';
import { useTransactionCategories } from '../hooks/useTransactionCategories';

const CreateTransactionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [copyTransaction, setCopyTransaction] = useState<Transaction | undefined>(undefined);
  const { directions, loading: directionsLoading } = useTransactionDirections();
  const { types: transactionTypes, loading: transactionTypesLoading } = useTransactionTypes();
  const { categories: transactionCategories, loading: transactionCategoriesLoading } = useTransactionCategories();

  const projectId = searchParams.get('projectId') ? parseInt(searchParams.get('projectId')!) : undefined;
  const copyId = searchParams.get('copyId') ? parseInt(searchParams.get('copyId')!) : undefined;

  useEffect(() => {
    const fetchData = async () => {
      setIsDataLoading(true);
      try {
        // Fetch customers
        const customersData = await customersAPI.getCustomers();
        setCustomers(customersData);

        // Fetch transaction to copy if copyId is provided
        if (copyId) {
          const transactionData = await transactionsAPI.getTransaction(copyId);
          setCopyTransaction(transactionData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchData();
  }, [copyId]);

  const handleSubmit = async (transaction: CreateTransaction) => {
    setIsLoading(true);
    try {
      await transactionsAPI.createTransaction(transaction);
      // Go back to previous page
      navigate(-1);
    } catch (error) {
      console.error('Error creating transaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Go back to previous page
    navigate(-1);
  };

  // Find the customer and project for preselection
  let preselectedCustomerId: number | undefined;
  let preselectedProjectId: number | undefined;

  if (copyTransaction) {
    // When copying, use the transaction's project
    preselectedProjectId = copyTransaction.projectId;
    const customer = customers.find(c => 
      c.projects.some(p => p.id === copyTransaction.projectId)
    );
    preselectedCustomerId = customer?.id;
  } else if (projectId) {
    // When creating from project link, use the provided projectId
    preselectedProjectId = projectId;
    const customer = customers.find(c => 
      c.projects.some(p => p.id === projectId)
    );
    preselectedCustomerId = customer?.id;
  }

  const isCopyLookupLoading = copyId !== undefined && (
    directionsLoading ||
    transactionTypesLoading ||
    transactionCategoriesLoading
  );

  if (isDataLoading || isCopyLookupLoading) {
    return (
      <div className="fade-in">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ color: 'var(--text-muted)' }}>Loading transaction...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <TransactionForm
        transaction={copyTransaction}
        customers={customers}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
        preselectedProjectId={preselectedProjectId}
        preselectedCustomerId={preselectedCustomerId}
        mode={copyTransaction ? 'copy' : 'create'}
        directionOptions={directions}
        typeOptions={transactionTypes}
        categoryOptions={transactionCategories}
      />
    </div>
  );
};

export default CreateTransactionPage;
